import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";
import { captureException } from "@sentry/browser";
import { toast } from "sonner";
import { isProviderNotSharedError } from "@/features/provider/provider-not-shared-error";

/**
 * Backstop for the 2FA gate. The route guards proactively redirect 2FA-required
 * users whose second factor isn't verified, but if any query fails with the
 * API's MFA_REQUIRED marker (e.g. a session downgraded mid-use), force them to
 * the MFA setup screen. A hard redirect avoids importing the router here (that
 * would create an import cycle) and re-runs the route guards from scratch.
 */
const redirectToMfaSetup = (error: unknown) => {
  const message = (error as { message?: string })?.message;
  const code = (error as { data?: { code?: string } })?.data?.code;
  if (message !== "MFA_REQUIRED" || code !== "FORBIDDEN") {
    return;
  }
  if (window.location.pathname.startsWith("/auth/mfa")) {
    return;
  }
  const redirect = encodeURIComponent(
    window.location.pathname + window.location.search,
  );
  window.location.assign(`/auth/mfa?redirect=${redirect}`);
};

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: redirectToMfaSetup,
  }),
  mutationCache: new MutationCache({
    onError: (error) => {
      redirectToMfaSetup(error);
      if (!navigator.onLine) {
        toast.error(
          "You appear to be offline. Please check your internet connection.",
        );
      }
      // Expected user-state rejection, explained to the user by its dialog.
      if (isProviderNotSharedError(error)) {
        return;
      }
      // Throwing from this callback creates a separate unhandled rejection,
      // even when the caller handles the mutation failure.
      captureException(error);
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000, // 1 minute
    },
    mutations: {
      networkMode: "always", // Fail immediately when offline instead of hanging
    },
  },
});
